import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Route as RouteIcon, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Seo, { orgSchema } from "@/components/Seo";

const SITE = "https://signiastylettoix.com";

export default function LearnPath() {
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setP(null);
        api.get(`/learn/paths/${slug}`)
            .then((r) => { if (!cancelled) setP(r.data); })
            .catch(() => { if (!cancelled) setError(true); });
        window.scrollTo(0, 0);
        return () => { cancelled = true; };
    }, [slug]);

    if (error) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Path not found. <Link className="underline ml-2" to="/learn">Knowledge Centre</Link></div>;
    if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Loading…</div>;

    return (
        <>
            <Seo title={`${p.title} — Guided Learning Path`} description={p.description}
                canonical={`${SITE}/learn/paths/${p.slug}`} schema={{ "@context": "https://schema.org", "@graph": [orgSchema()] }} />
            <section className="pt-12 md:pt-16 pb-16">
                <div className="container-x max-w-[760px]">
                    <nav className="text-[12.5px] text-[#4a5568] mb-6">
                        <Link to="/" className="hover:text-[#003E4D]">Home</Link><span className="mx-2 text-[#9CA3AF]">/</span>
                        <Link to="/learn" className="hover:text-[#003E4D]">Knowledge Centre</Link><span className="mx-2 text-[#9CA3AF]">/</span>
                        <span className="text-[#1a1a1a]">Learning path</span>
                    </nav>
                    <p className="eyebrow inline-flex items-center gap-2"><RouteIcon size={12} /> Guided Learning Path</p>
                    <h1 className="font-serif text-[34px] md:text-[46px] leading-[1.1] mt-4" data-testid="path-h1">{p.title}</h1>
                    <p className="mt-4 text-[16px] text-[#4a5568]">{p.description} Follow the steps in order — each takes a few minutes.</p>
                    <div className="mt-10 space-y-4" data-testid="path-steps">
                        {p.articles.map((a, i) => (
                            <Link key={a.slug} to={`/learn/${a.slug}`} className="flex items-start gap-4 p-5 bg-white border border-[#e2ddd4] rounded-2xl hover:border-[#003E4D] transition">
                                <span className="w-9 h-9 rounded-full bg-[#003E4D] text-white flex items-center justify-center font-serif shrink-0">{i + 1}</span>
                                <span>
                                    <span className="block font-medium text-[16px]">{a.title}</span>
                                    <span className="block text-[13px] text-[#4a5568] mt-1">{a.read_minutes} min read</span>
                                </span>
                            </Link>
                        ))}
                    </div>
                    {p.cta && (
                        <Link to={p.cta.url} className="btn-primary mt-10 inline-flex" data-testid="path-cta">{p.cta.label} <ArrowRight size={16} /></Link>
                    )}
                </div>
            </section>
        </>
    );
}
